import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Collapse,
  IconButton,
  CircularProgress,
  Button
} from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DiffView, countChanges } from './DiffView';

const PANEL = {
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 2,
  mb: 1,
  bgcolor: 'rgba(255,255,255,0.02)'
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('portalToken')}` });

/**
 * Validation des propositions de l'agent de mise en forme.
 *
 * L'agent n'a rien écrit : chaque document est proposé, l'utilisateur voit le
 * diff et décide. C'est le vrai contrôle qu'on veut sur de la prose qui part
 * chez un client — et il se déclenche à tous les coups, contrairement à une
 * question que le modèle ne pose jamais.
 *
 * Le contenu actuel est relu au moment de la validation, pas figé au moment de
 * la proposition : le diff montre l'écart avec ce qui est réellement en place.
 *
 * La relecture de chaque document est indépendante (`Promise.allSettled`) :
 * un document supprimé ou devenu inaccessible entre la proposition et la
 * validation n'empêche pas de décider sur les autres. Il apparaît seul en
 * « indisponible » et sort de la sélection par défaut — on ne valide jamais
 * une proposition sans avoir pu en montrer le diff.
 */
function FormattingValidation({ propositions, ignores = [], busy, onApply }) {
  const [actuels, setActuels] = useState(null);
  const [echecs, setEchecs] = useState({});
  const [acceptes, setAcceptes] = useState(() => new Set(propositions.map((p) => p.documentId)));
  const [ouvert, setOuvert] = useState(() => propositions[0]?.documentId || null);

  useEffect(() => {
    let annule = false;
    (async () => {
      const resultats = await Promise.allSettled(propositions.map(async (proposition) => {
        const res = await fetch(`/api/documents/${proposition.documentId}/content`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Lecture impossible (${res.status})`);
        return (await res.json()).content || '';
      }));
      if (annule) return;

      const contenus = {};
      const problemes = {};
      resultats.forEach((resultat, index) => {
        const { documentId } = propositions[index];
        if (resultat.status === 'fulfilled') contenus[documentId] = resultat.value;
        else problemes[documentId] = resultat.reason?.message || 'Lecture impossible.';
      });
      setActuels(contenus);
      setEchecs(problemes);
    })();
    return () => { annule = true; };
  }, [propositions]);

  // Un document dont la lecture a echoue ne peut pas etre valide sans diff :
  // on le retire de l'acceptation par defaut plutot que de risquer d'ecrire
  // a l'aveugle si l'utilisateur ne remarque pas le panneau "indisponible".
  useEffect(() => {
    const enEchec = Object.keys(echecs);
    if (enEchec.length === 0) return;
    setAcceptes((prev) => {
      let modifie = false;
      const suivant = new Set(prev);
      for (const documentId of enEchec) {
        if (suivant.delete(documentId)) modifie = true;
      }
      return modifie ? suivant : prev;
    });
  }, [echecs]);

  const compteurs = useMemo(() => {
    if (!actuels) return {};
    return Object.fromEntries(
      propositions
        .filter((p) => actuels[p.documentId] !== undefined)
        .map((p) => [p.documentId, countChanges(actuels[p.documentId], p.content)])
    );
  }, [actuels, propositions]);

  const basculer = (documentId) => setAcceptes((prev) => {
    const suivant = new Set(prev);
    if (suivant.has(documentId)) suivant.delete(documentId);
    else suivant.add(documentId);
    return suivant;
  });

  if (!actuels) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">Chargement des documents…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {propositions.length} correction(s) préparée(s). Rien n'est encore écrit.
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
        Rouge = contenu actuel, vert = proposition. Décochez ce que vous ne voulez pas.
      </Typography>

      {propositions.map((proposition) => {
        const probleme = echecs[proposition.documentId];
        const estOuvert = ouvert === proposition.documentId;

        if (probleme) {
          return (
            <Box key={proposition.documentId} sx={{ ...PANEL, borderColor: 'rgba(239,68,68,0.25)' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, p: 1 }}>
                <Checkbox
                  size="small"
                  checked={false}
                  disabled
                  inputProps={{ 'aria-label': `Proposition pour ${proposition.section} indisponible` }}
                />
                <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{proposition.section}</Typography>
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    Contenu actuel indisponible ({probleme}) : impossible de montrer le diff, cette
                    proposition ne peut pas être validée ici. Les autres documents restent décidables.
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        }

        return (
          <Box key={proposition.documentId} sx={PANEL}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, p: 1 }}>
              <Checkbox
                size="small"
                checked={acceptes.has(proposition.documentId)}
                onChange={() => basculer(proposition.documentId)}
                disabled={busy}
                inputProps={{ 'aria-label': `Accepter la proposition pour ${proposition.section}` }}
              />
              <Box
                sx={{ flex: 1, minWidth: 0, cursor: 'pointer', pt: 0.5 }}
                onClick={() => setOuvert(estOuvert ? null : proposition.documentId)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{proposition.section}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {proposition.summary || 'mise en forme'}
                  {' · '}
                  {compteurs[proposition.documentId]} ligne(s) de différence
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setOuvert(estOuvert ? null : proposition.documentId)}
                aria-label={estOuvert ? `Réduire le diff de ${proposition.section}` : `Afficher le diff de ${proposition.section}`}
              >
                {estOuvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </IconButton>
            </Box>

            <Collapse in={estOuvert} unmountOnExit>
              <DiffView
                before={actuels[proposition.documentId] || ''}
                after={proposition.content}
                contextLines={3}
                sx={{ maxHeight: '45vh', borderTop: '1px solid rgba(255,255,255,0.06)' }}
              />
            </Collapse>
          </Box>
        );
      })}

      {ignores.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Laissés tels quels par l'agent : {ignores.map((f) => f.section).join(' · ')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          disabled={busy || acceptes.size === 0}
          onClick={() => onApply([...acceptes])}
        >
          {busy ? 'Écriture…' : `Appliquer ${acceptes.size} document(s)`}
        </Button>
      </Box>
    </Box>
  );
}

export default FormattingValidation;
